
import { defineComponent, ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

import { hideModal } from "@/core/helpers/dom";
import ApiService from "@/core/services/ApiService";

interface Connection {
  id: string;
  conn_name: string;
  scheme: string;
  host: string;
  domain: string;
  auth_type: string;
  payload_type: string;
  response_type: string;
  enabled: string;
  conn_status: string;
  category: string;
  created: string;
  modified: string;
  logo_url: string;
}

interface Endpoint {
  id: string;
  connection_id: string;
  endpoint_name: string;
  method: string;
  custom_host: string;
  endpoint_path: string;
  enabled: string;
  endpoint_status: string;
  parameters: string;
  created: string;
  modified: string;
}

interface Api {
  merchant_id: string;
  connection_id: string;
  endpoint_id: string;
  parameters: string;
  request_parameters: string;
  alias_name: string;
}

interface Incoming {
  id: string;
  transaction_found: string;
  details_populated: string;
  dispute_url: string;
  // eslint-disable-next-line
  card_holder: any;
  // eslint-disable-next-line
  transaction: any;
  // eslint-disable-next-line
  order_details: any;
  merchant_id: string;
  reason: string;
  // eslint-disable-next-line
  dispute_cycle: any;
  connection_id: string;
  found_date: string;
  populated_date: string;
  sent_date: string;
  reason_condition: string;
}

export default defineComponent({
  name: "search-api-modal",
  components: {},
  emits: ["reload-incoming"],
  setup(props, { emit }) {
    const route = useRoute();
    const searchApiModalRef = ref<HTMLElement | null>(null);
    const submitButton = ref<HTMLButtonElement | null>(null);
    const connections = ref<Connection[]>([]);
    const endpoints = ref<Endpoint[]>([]);
    const apis = ref<Api[]>([]);
    const connEndOptions = ref<unknown[]>([]);
    const selectedConnEnd = ref([]);
    // eslint-disable-next-line
    const parameters = ref<any>(null);
    const incoming = ref<Incoming>({
      id: "",
      transaction_found: "0",
      details_populated: "0",
      dispute_url: "",
      card_holder: {},
      transaction: {
        action: {},
      },
      order_details: {
        items: [],
      },
      merchant_id: "",
      reason: "",
      dispute_cycle: {},
      connection_id: "",
      found_date: "0000-00-00 00:00:00",
      populated_date: "0000-00-00 00:00:00",
      sent_date: "0000-00-00 00:00:00",
      reason_condition: "",
    });

    const startEndDate = ref("");

    const shortcuts = ref([
      {
        text: "last 30 days",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
          return [start, end];
        },
      },
      {
        text: "last 60 days",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 60);
          return [start, end];
        },
      },
      {
        text: "last 90 days",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
          return [start, end];
        },
      },
      {
        text: "last 120 days",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 120);
          return [start, end];
        },
      },
      {
        text: "last 180 days",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setTime(start.getTime() - 3600 * 1000 * 24 * 180);
          return [start, end];
        },
      },
    ]);

    const selConnEnd = (value) => {
      const selectedApi = apis.value.find(
        (api) => api.connection_id === value[0] && api.endpoint_id === value[1]
      );

      if (selectedApi) {
        parameters.value = JSON.parse(selectedApi.request_parameters);
        delete parameters.value.start_date;
        delete parameters.value.end_date;
      } else {
        parameters.value = null;
      }
    };

    onMounted(async () => {
      setTimeout(async () => {
        const { data } = await ApiService.get(
          "apis/connections?limit=100&group[category]=Gateway"
        );
        if (data.success) {
          connections.value = [...data.data];
        }

        let res = await ApiService.get("apis/endpoints?limit=100");
        if (res.data.success) {
          endpoints.value = [...res.data.data];
        }

        res = await ApiService.get(
          `chargebacks/incoming?id=${getIncomingId.value}`
        );
        incoming.value = res.data.data[0];

        res = await ApiService.get(
          `apis/apis.merchants?merchant_id=${incoming.value.merchant_id}&category=Gateway&limit=100`
        );
        if (res.data.success) {
          if (res.data.data !== null) {
            apis.value = [...res.data.data];
          }
        }

        connections.value = connections.value.filter((conn) => {
          const found = apis.value.find((api) => api.connection_id === conn.id);
          if (found === undefined) {
            return false;
          }

          return true;
        });

        connEndOptions.value = connections.value.map((conn) => {
          const connApis = apis.value.filter(
            (api) => api.connection_id === conn.id
          );
          const connEndpoints = connApis.map((api) => {
            const endpoint = endpoints.value.find(
              (endpoint) => endpoint.id === api.endpoint_id
            );
            return {
              value: endpoint?.id,
              label: api.alias_name,
            };
          });

          return {
            value: conn.id,
            label: conn.conn_name,
            children: connEndpoints,
          };
        });
      }, 1000);
    });

    const getIncomingId = computed(() => {
      return route.params.id;
    });

    const formSubmit = async () => {
      if (
        !selectedConnEnd.value[0] ||
        startEndDate.value === "" ||
        startEndDate.value === null
      ) {
        Swal.fire({
          text: "Please input the fields!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "OK!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });

        return;
      }

      const endDate = new Date(startEndDate.value[1]);
      const today = new Date();
      if (endDate > today) {
        Swal.fire({
          text: "Date can not be greater than today!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "OK!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });

        return;
      }

      // eslint-disable-next-line
      submitButton.value!.disabled = true;
      // Activate indicator
      submitButton.value?.setAttribute("data-kt-indicator", "on");

      // eslint-disable-next-line
      const params: any = parameters.value;
      const parameter_values = {
        ...params,
        start_date: startEndDate.value[0],
        end_date: startEndDate.value[1],
      };

      // eslint-disable-next-line
      const payload: any = {
        endpoint_id: selectedConnEnd.value[1],
        merchant_id: incoming.value.merchant_id,
        parameter_values,
      };

      try {
        const res = await ApiService.post(
          `chargebacks/incoming/${getIncomingId.value}?action=api_search`,
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "API Successfully searched!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(function () {
            hideModal(searchApiModalRef.value);
            emit("reload-incoming");
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButton.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButton.value!.disabled = false;
    };

    return {
      submitButton,
      connEndOptions,
      selectedConnEnd,
      shortcuts,
      startEndDate,
      formSubmit,
      searchApiModalRef,
      selConnEnd,
      parameters,
    };
  },
});
